import React from 'react';
import { Hammer, Home, Wrench, PaintBucket, Building2, Drill } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const ServicesPage = () => {
  const mainServices = [
    {
      icon: Hammer,
      title: 'Builders',
      description: 'Professional building services for all your construction needs',
      image: 'https://customer-assets.emergentagent.com/job_capital-roofing/artifacts/dlzi22jv_Photo%2020-01-2026%2C%2020%2051%2055.jpg',
      details: [
        'Brickwork and masonry',
        'Carpentry and joinery',
        'Paving and patios',
        'Garden walls and fencing',
        'Structural alterations',
        'General building repairs'
      ]
    },
    {
      icon: Home,
      title: 'House Extensions',
      description: 'Add space and value to your property with our extension services',
      image: 'https://customer-assets.emergentagent.com/job_capital-roofing/artifacts/1n3v1oyl_House-1.jpg',
      details: [
        'Single and double-storey extensions',
        'Kitchen extensions',
        'Living room extensions',
        'Complete design service',
        'Building regulations approval',
        'Project management from start to finish'
      ]
    },
    {
      icon: Wrench,
      title: 'Renovations & Restorations',
      description: 'Transform your property with comprehensive renovation services',
      image: 'https://customer-assets.emergentagent.com/job_capital-roofing/artifacts/2gqw5fxa_Photo-20-01-2026%2C-20-49-59-%282%29.jpg',
      details: [
        'Complete property renovations',
        'Period property restoration',
        'Bathroom and wetroom installations',
        'Internal alterations',
        'Structural repairs',
        'Modernisation projects'
      ]
    },
    {
      icon: PaintBucket,
      title: 'Conversions',
      description: 'Maximize your property space with expert conversion services',
      image: 'https://customer-assets.emergentagent.com/job_capital-roofing/artifacts/cz20b24k_Photo-20-01-2026%2C-20-49-59-%285%29.jpg',
      details: [
        'Loft conversions',
        'Garage conversions',
        'Basement conversions',
        'Design and planning',
        'Insulation and ventilation',
        'Electrical and plumbing work'
      ]
    }
  ];

  const roofingServices = [
    {
      title: 'New Roof Installations',
      description: 'Whether you favour a pitched roof or a flat roof, we have the tools, skills, and experience to ensure a first-rate installation every time.',
      features: [
        'Pitched roofs',
        'Flat roofs',
        'Tile and slate roofing',
        'EPDM rubber roofing',
        'Fascias and soffits',
        'Guttering systems'
      ]
    },
    {
      title: 'Roof Repairs',
      description: 'Fast and effective roof repairs for all types of roofing issues. From missing tiles to chimney repairs, we\'re here to help.',
      features: [
        'Missing or damaged tiles',
        'Leak detection and repair',
        'Chimney repairs and repointing',
        'Ridge tile repairs',
        'Flashing repairs',
        'Emergency roof repairs'
      ]
    },
    {
      title: 'Roof Maintenance',
      description: 'Regular maintenance to keep your roof in top condition and prevent costly repairs in the future.',
      features: [
        'Roof inspections',
        'Gutter cleaning',
        'Moss removal',
        'Minor repairs',
        'Preventive maintenance',
        'Weather damage assessment'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-[#4ade80]/5 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Our <span className="text-[#4ade80]">Services</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Comprehensive building and renovation services for residential and commercial properties. As a CITB-registered company based in Burbage, we serve Hinckley, South Leicestershire, Nuneaton and surrounding areas with over 20 years of expertise.
          </p>
        </div>
      </section>

      {/* Main Services */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12">
            {mainServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card
                  key={index}
                  className="bg-[#1a1a1a] border-gray-800 overflow-hidden hover:border-[#4ade80] transition-all duration-300"
                >
                  <div className="grid md:grid-cols-2 gap-0">
                    <div className={`relative h-80 md:h-auto ${index % 2 === 0 ? 'md:order-1' : 'md:order-2'}`}>
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    </div>
                    <CardContent className={`p-8 md:p-12 flex flex-col justify-center ${index % 2 === 0 ? 'md:order-2' : 'md:order-1'}`}>
                      <div className="w-16 h-16 bg-[#4ade80] rounded-lg flex items-center justify-center mb-6">
                        <IconComponent className="text-black" size={32} />
                      </div>
                      <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        {service.title}
                      </h2>
                      <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                        {service.description}
                      </p>
                      <ul className="space-y-3 mb-6">
                        {service.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start text-gray-300">
                            <div className="w-1.5 h-1.5 bg-[#4ade80] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Roofing Services Section */}
      <section className="section-padding bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="w-16 h-16 bg-[#4ade80] rounded-lg flex items-center justify-center mx-auto mb-6">
              <Building2 className="text-black" size={32} />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Roofing <span className="text-[#4ade80]">Services</span>
            </h2>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto leading-relaxed">
              Expert roofing contractors providing comprehensive roofing solutions for all your needs
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {roofingServices.map((service, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-[#0a0a0a] border border-gray-800 rounded-lg overflow-hidden hover:border-[#4ade80] transition-colors"
              >
                <AccordionTrigger className="px-6 py-6 text-left hover:no-underline">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {service.title}
                    </h3>
                    <p className="text-gray-400">{service.description}</p>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-gray-800">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start">
                        <Drill className="text-[#4ade80] mr-3 mt-1 flex-shrink-0" size={16} />
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-br from-[#4ade80]/10 to-transparent border-[#4ade80]/30">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Need a Quote for Your Project?
              </h2>
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                Contact us today for a free, no-obligation quote. Our experienced team is ready to discuss your requirements and provide expert advice.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  asChild
                  size="lg"
                  className="bg-[#4ade80] hover:bg-[#22c55e] text-black font-semibold"
                >
                  <Link to="/contact">Get Free Quote</Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-black"
                >
                  <a href="tel:07738965968">Call: 07738 965 968</a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;